import { z } from 'zod';

/**
 * Manufacturing (التصنيع) — recipes (BOMs) and production orders.
 *
 * A BOM is ONE recipe per finished item: components (any items, with the unit
 * snapshot trio) and the batch yield. A production order is the document that
 * actually moves stock — assemble consumes components and receives the
 * finished item at the rolled-up cost; disassemble is the mirror. Quantities
 * are 4-dp decimal STRINGS end to end, the codebase convention.
 */

const decimalString = z.string().regex(/^\d+(\.\d{1,4})?$/);
const positiveDecimalString = decimalString.refine((v) => Number(v) > 0, 'must be > 0');

// ---- BOM (recipe) ----

export const BomLineInputSchema = z.object({
  componentItemId: z.string().uuid(),
  /** Unit snapshot trio; base consumed = quantity × unitFactor. */
  itemUnitId: z.string().uuid().nullable().optional(),
  unitName: z.string().max(64).nullable().optional(),
  unitFactor: positiveDecimalString.default('1'),
  /** Per BATCH, in the chosen unit. */
  quantity: positiveDecimalString,
});
export type BomLineInput = z.infer<typeof BomLineInputSchema>;

export const BomUpsertSchema = z.object({
  /** Base units one batch yields. */
  outputQuantity: positiveDecimalString.default('1'),
  /** Default labor/overhead per RUN — copied onto new orders, editable there. */
  extraCost: decimalString.default('0'),
  extraCostCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  isActive: z.boolean().default(true),
  notes: z.string().max(500).nullable().optional(),
  lines: z.array(BomLineInputSchema).min(1).max(200),
});
export type BomUpsert = z.infer<typeof BomUpsertSchema>;

export interface BomLine {
  id: string;
  lineNo: number;
  componentItemId: string;
  componentCode: string;
  componentName: string;
  componentUom: string;
  itemUnitId: string | null;
  unitName: string | null;
  unitFactor: string;
  quantity: string;
}

export interface Bom {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  outputQuantity: string;
  extraCost: string;
  extraCostCurrencyCode: string | null;
  isActive: boolean;
  notes: string | null;
  updatedAt: string;
  lines: BomLine[];
}

export interface BomSummary {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  outputQuantity: string;
  componentCount: number;
  isActive: boolean;
  updatedAt: string;
}

// ---- Production orders ----

export const ProductionOrderKindSchema = z.enum(['assemble', 'disassemble']);
export type ProductionOrderKind = z.infer<typeof ProductionOrderKindSchema>;

/** draft → posted → voided; draft → cancelled. */
export type ProductionOrderStatus = 'draft' | 'posted' | 'voided' | 'cancelled';

export const ProductionOrderLineInputSchema = z.object({
  itemId: z.string().uuid(),
  /** Consume/receive warehouse override; null → the header's warehouse. */
  warehouseId: z.string().uuid().nullable().optional(),
  itemUnitId: z.string().uuid().nullable().optional(),
  unitName: z.string().max(64).nullable().optional(),
  unitFactor: positiveDecimalString.default('1'),
  /** TOTAL for the order, in the chosen unit. */
  quantity: positiveDecimalString,
});
export type ProductionOrderLineInput = z.infer<typeof ProductionOrderLineInputSchema>;

export const ProductionOrderCreateSchema = z.object({
  kind: ProductionOrderKindSchema.default('assemble'),
  itemId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  bomId: z.string().uuid().nullable().optional(),
  itemUnitId: z.string().uuid().nullable().optional(),
  unitName: z.string().max(64).nullable().optional(),
  unitFactor: positiveDecimalString.default('1'),
  /** Output (assemble) / input (disassemble) quantity, in the chosen unit. */
  quantity: positiveDecimalString,
  extraCost: decimalString.default('0'),
  extraCostCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
  /** Component lines. Omitted with a bomId ⇒ the server explodes the recipe. */
  lines: z.array(ProductionOrderLineInputSchema).max(200).optional(),
});
export type ProductionOrderCreate = z.infer<typeof ProductionOrderCreateSchema>;

export const ProductionOrderUpdateSchema = ProductionOrderCreateSchema.partial();
export type ProductionOrderUpdate = z.infer<typeof ProductionOrderUpdateSchema>;

export const ProductionOrderPostSchema = z.object({
  /** The ONE currency the finished cost lands in; omitted ⇒ resolved by ladder. */
  costCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).optional(),
  occurredAt: z.string().datetime().optional(),
});
export type ProductionOrderPost = z.infer<typeof ProductionOrderPostSchema>;

export interface ProductionOrderLine {
  id: string;
  lineNo: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  itemUom: string;
  warehouseId: string | null;
  itemUnitId: string | null;
  unitName: string | null;
  unitFactor: string;
  quantity: string;
  /** Frozen at post — the level's average in its OWN currency. */
  avgCost: string | null;
  avgCostCurrencyCode: string | null;
  /** Frozen at post — per base unit, converted into the order's cost currency. */
  costInOrderCurrency: string | null;
}

export interface ProductionOrderSummary {
  id: string;
  docNumber: string;
  status: ProductionOrderStatus;
  kind: ProductionOrderKind;
  itemId: string;
  itemCode: string;
  itemName: string;
  warehouseId: string;
  warehouseName: string;
  quantity: string;
  unitName: string | null;
  unitFactor: string;
  unitCost: string | null;
  totalComponentCost: string | null;
  costCurrencyCode: string | null;
  postedAt: string | null;
  createdAt: string;
}

export interface ProductionOrder extends ProductionOrderSummary {
  bomId: string | null;
  itemUnitId: string | null;
  extraCost: string;
  extraCostCurrencyCode: string | null;
  notes: string | null;
  voidedAt: string | null;
  lines: ProductionOrderLine[];
}
